/**
 * ZAGX v2 — Sentry Error Monitoring
 * Free tier: 5K errors/month — more than enough for personal use.
 *
 * Setup: Create a project at https://sentry.io → Settings → DSN
 * Then paste your DSN below or set VITE_SENTRY_DSN in .env
 */
import * as Sentry from '@sentry/browser';
import { scrubUrl } from './secrets.js';

const DSN = import.meta.env.VITE_SENTRY_DSN || '';

let _initialized = false;

export function initSentry() {
  if (_initialized || !DSN) {
    if (!DSN) console.info('[Sentry] No DSN configured — error monitoring disabled. Set VITE_SENTRY_DSN in .env');
    return;
  }

  Sentry.init({
    dsn: DSN,
    environment: import.meta.env.MODE || 'production',
    release: `zagx@${__APP_VERSION__ || '0.8.0'}`,

    // Performance — keep sampling low for free tier
    tracesSampleRate: 0,

    // Only capture actual errors, not breadcrumbs noise
    beforeSend(event) {
      // Skip known non-critical errors
      const msg = event.exception?.values?.[0]?.value || '';
      const msgLower = msg.toLowerCase();
      if (msg.includes('ResizeObserver loop') || msg.includes('Script error') || msgLower.includes('service worker') || msgLower.includes('serviceworker')) return null;
      // Network errors to Supabase — transient, handled by circuit breaker + retry
      if (msgLower.includes('failed to fetch') && msgLower.includes('supabase')) return null;
      if (msgLower.includes('failed to fetch') && msg.includes('dtvpywjwmpaggtgvskwb')) return null;
      // Generic network failures (AbortError from timeouts, fetch rejections on page close)
      if (msgLower.includes('the user aborted a request') || msgLower.includes('aborterror')) return null;
      if (msg === 'TypeError: Failed to fetch') return null;
      // …and the same scrub on the event itself: a request URL and an exception message can
      // each carry the query string that beforeBreadcrumb just cleaned out of the trail.
      if (event.request && typeof event.request.url === 'string') {
        event.request.url = scrubUrl(event.request.url);
      }
      (event.exception?.values || []).forEach(v => {
        if (typeof v.value === 'string' && v.value.includes('?')) {
          v.value = v.value.replace(/https?:\/\/\S+/g, m => scrubUrl(m));
        }
      });
      return event;
    },

    // ⚠ SEC-002 — A BREADCRUMB IS A PAYLOAD THAT LEAVES THE DEVICE.
    //
    // `breadcrumbsIntegration({ fetch: true })` below records the URL of every fetch, and
    // `prices.js` sends the Twelve Data key as `&apikey=<key>`. With no hook here, the last
    // thirty request URLs ride along with any error that IS reported — the key among them, to a
    // third party, on an event the owner never sees. `beforeSend` filtered by MESSAGE and never
    // looked at what was attached to the message.
    //
    // Redaction happens at this boundary rather than at the call site on purpose: it covers
    // every present and future integration, including ones added by someone who has not read
    // this comment.
    beforeBreadcrumb(bc) {
      if (!bc) return bc;
      if (bc.data && typeof bc.data.url === 'string') bc.data.url = scrubUrl(bc.data.url);
      if (typeof bc.message === 'string' && bc.message.includes('?')) bc.message = scrubUrl(bc.message);
      return bc;
    },

    // Limit breadcrumbs to keep payload small
    maxBreadcrumbs: 30,

    // Integrations
    integrations: [
      Sentry.breadcrumbsIntegration({
        console: false,     // don't track console.log
        dom: true,          // track clicks
        fetch: true,        // track API calls
        history: true,      // track navigation
      }),
    ],
  });

  _initialized = true;
  console.info('[Sentry] Error monitoring active');
}

/**
 * Manually capture an error with context
 */
export function captureError(error, context = {}) {
  if (!_initialized) {
    console.error('[Sentry:offline]', error, context);
    return;
  }
  Sentry.withScope(scope => {
    Object.entries(context).forEach(([k, v]) => scope.setExtra(k, v));
    Sentry.captureException(error);
  });
}

/**
 * Set user context after login
 */
export function setSentryUser(userId, email) {
  if (!_initialized) return;
  Sentry.setUser({ id: userId, email });
}

/**
 * Add custom breadcrumb for financial operations
 */
export function addBreadcrumb(category, message, data = {}) {
  if (!_initialized) return;
  Sentry.addBreadcrumb({ category, message, data, level: 'info' });
}
